<script setup>
  import { ref, computed, defineAsyncComponent, getCurrentInstance } from 'vue'

  const cashModalTextHTML = `
    <h2>Получение наличных в кассе РНКО «СТАТЕРА» (ООО)</h2>
    <p>Для получения наличных денежных средств необходимо лично обратиться в отделение РНКО «СТАТЕРА» (ООО) по адресу:</p>
    <address>
      384900, Республика Абхазия,<br>
      г. Сухум, ул. Лакоба, д. 34
    </address>
    <p>При обращении в кассу необходимо предоставить:</p>
    <ul>
      <li>Паспорт на имя получателя.</li>
      <li>Информацию для оформления заявки:
        <ul>
          <li>Фамилия, имя, отчество получателя;</li>
          <li>Сумма к получению;</li>
          <li>Номер квитанции.</li>
        </ul>
      </li>
    </ul>
    <p>Сотрудник кассы оформит заявку на основании предоставленных данных. После проверки документов будет осуществлена выдача наличных денежных средств.</p>
    <p><strong>Важно:</strong> паспортные данные должны совпадать с информацией, указанной в электронном кошельке.</p>
  `;

  const BaseButton = defineAsyncComponent(() =>
      import(/* webpackChunkName: "components/base/buttons/BaseButton.vue" */ '@/components/base/buttons/BaseButton.vue')
  )
  const BackBtn = defineAsyncComponent(() =>
      import(/* webpackChunkName: "components/base/buttons/BackBtn.vue" */ '@/components/base/buttons/BackBtn.vue')
  )
  const TextModal = defineAsyncComponent(() =>
      import(/* webpackChunkName: "components/TextModal.vue" */ '@/components/TextModal.vue')
  )
  const PayModal = defineAsyncComponent(() =>
      import(/* webpackChunkName: "components/PayModal.vue" */ '@/components/PayModal.vue')
  )
  const WalletList = defineAsyncComponent(() =>
      import(/* webpackChunkName: "components/WalletList.vue" */ '@/components/WalletList.vue')
  )

  import { useStore } from 'vuex'
  const vuexStore = useStore()

  import { settings } from "../../appType/settings.js";
  const envName = settings.env.VUE_APP_ENV_NAME

  const instance = getCurrentInstance();
  const $http = instance.proxy.$http
  const text = instance.proxy.text

  const modalStage = ref('enterPin')
  const amount = ref('')
  const card = ref('')
  const address = ref('')
  const errorMessage = ref('')
  const modalActive = ref(false)
  const withdrawalDisabled = ref(false)
  const sbpModalActive = ref(false)
  const cashModalActive = ref(false)
  const cardModalActive = ref(false)
  const cashModalText = cashModalTextHTML

  const activeWallet = computed(() => {
    return vuexStore.state.currentWallet
  })
  const authToken = computed(() => {
    return vuexStore.state.authToken
  })
  const formattedCard = computed({
    get() {
      return card.value.replace(/\s+/g, '').replace(/(\d{4})/g, '$1 ').trim();
    },
    set(v) {
      card.value = v.replace(/\s/g, '');
    }
  })

  function formatCard() {
    card.value = card.value.replace(/\D/g, '').substring(0, 16);
  }
  function showModal () {
    modalActive.value = true;
    modalStage.value = 'enterPin';
  }
  async function doWithdrawal(pinCode) {
    modalStage.value = 'waiting';

    const withdrawalData = {
      "amount": amount.value,
      "currency_code": activeWallet.value?.currency_code,
      "pin": pinCode
    }

    if (activeWallet.value?.currency_code === 'USDC') {
      withdrawalData.address = address.value
    }

    const { data } = await $http.post(
        '/v2/wallet/withdrawal',
        withdrawalData,
        {
          "headers": {
            "Authorization": "Token " + authToken.value
          },
          "validateStatus": status => (status >= 200 && status < 500)
        },
    );

    if (data.status === "error") {
      modalStage.value = 'failed';
      errorMessage.value = data.message
    } else {
      modalStage.value = 'success';
    }
  }

  function closeModal () {
    modalActive.value = false;
  }

</script>

<template>
  <div class="wallet_withdrawal">
    <div>
      <div class="title">
        <div class="back-btn__wrapper">
          <BackBtn @click="$router.back()"/>
        </div>
        {{ text('wallet_withdrawal.select_account') }}
      </div>
      <div class="padding-wrapper">
        <WalletList />
      </div>
      <div class="input">
        <span>{{ text('wallet_withdrawal.amount') }}</span>
        <input type="text" v-model="amount" inputmode="numeric" pattern="[0-9\s]{13,19}" placeholder="0">
      </div>
    </div>
    <div v-if="envName !== 'grok'">
      <div class="divider" />
      <div class="actions-block">
        <BaseButton
          prefix
          grayscale-accent
          :disabled="!amount"
          @click="sbpModalActive = true"
        >
          <template #prefix>
            <img height="24" src="@/assets/images/sbp_logo.svg" alt="sbp" />
          </template>
          <template #label>
            {{ text('wallet_topup.methods.cbp') }}
          </template>
        </BaseButton>
        <BaseButton
          prefix
          grayscale-accent
          :disabled="!amount"
          @click="cardModalActive = true"
        >
          <template #prefix>
            <img height="24" src="@/assets/images/auth_card.png" alt="auth-card" />
          </template>
          <template #label>
            {{ text('wallet_withdrawal.to-card') }}
          </template>
        </BaseButton>
        <BaseButton
          v-if="envName === 'stater'"
          prefix
          grayscale-accent
          :disabled="!amount"
          @click="cashModalActive = true"
        >
          <template #prefix>
            <img height="24" src="@/assets/images/cash.svg" alt="cash" />
          </template>
          <template #label>
            {{ text('wallet_withdrawal.cash') }}
          </template>
        </BaseButton>

        <template v-if="sbpModalActive || cardModalActive || cashModalActive">
          <TextModal
              @closeModal="sbpModalActive = false"
              v-if="sbpModalActive"
              title=""
              text="Вывод по СБП временно недоступен"
          />
          <TextModal
              @closeModal="cardModalActive = false"
              v-if="cardModalActive"
              title=""
              text="Вывод на карту временно недоступен"
          />
          <TextModal
              @closeModal="cashModalActive = false"
              v-if="cashModalActive"
              title=""
              :text="cashModalText"
          />
        </template>
      </div>
    </div>

    <div v-if="envName === 'grok'">
      <div class="divider" />
      <div class="input" v-if="activeWallet?.currency_code === 'USDC'" style="margin: 0 auto 24px">
        <span>{{ text('wallet_withdrawal.usdc_etc_address') }}</span>
        <input type="text" v-model="address" inputmode="numeric">
      </div>
      <div class="input" style="margin: 0 auto 24px" v-else>
        <span>{{ text('wallet_withdrawal.card') }}</span>
        <input
            type="tel"
            v-model="formattedCard"
            @input="formatCard"
            inputmode="numeric"
            pattern="[0-9\s]{13,19}"
            autocomplete="cc-number"
            maxlength="19"
            placeholder="xxxx xxxx xxxx xxxx"
            required
        >
      </div>
      <div class="actions-block">
        <BaseButton
          :disabled="withdrawalDisabled || !amount || (activeWallet?.currency_code === 'USDC' && !address) || (activeWallet?.currency_code !== 'USDC' && !card) || activeWallet.balance < amount"
          @click="showModal()"
        >
          <template #label>
            {{ text('wallet_withdrawal.withdrawal_btn') }}
          </template>
        </BaseButton>
      </div>
    </div>
    <PayModal
      :stage="modalStage"
      :errorMessage="errorMessage"
      :sucessMessage="text('wallet_withdrawal.success_message')"
      @enterPin="doWithdrawal"
      @closeModal="closeModal()"
      v-if="modalActive"
      :pinCode="pinCode"
    />
  </div>
</template>

<style lang="scss" scoped>
  .wallet_withdrawal {
  padding: 24px;
  position: relative;
  height: 100%;
  min-height: 600px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 16px;

  .title {
    position: relative;

    color: var(--text-color-primary);
    font-family: "Rubik", sans-serif;
    font-size: 21px;
    font-style: normal;
    font-weight: 700;
    text-align: center;
    margin-bottom: 32px;

    .back-btn__wrapper {
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
    }
  }

  .input {
    display: flex;
    align-items: center;
    justify-content: space-between;

    background: #262329;
    margin: 32px auto 20px;
    padding: 16px;
    border-radius: 12px;
    border: 1px solid var(--element-bg);

    &:last-child {
      margin-bottom: 0;
    }

    span {
      color: var(--text-color-primary);
      font-family: "Rubik", sans-serif;
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: 135%;
      white-space: nowrap;
    }

    input {
      width: calc(100% - 70px);
      text-align: right;
      color: var(--text-color-primary);
      font-family: "Rubik", sans-serif;
      font-size: 18px;
      font-style: normal;
      font-weight: 500;
      line-height: normal;
      letter-spacing: 0.36px;
    }
  }

  .divider {
    width: 100%;
    height: 1px;
    background: var(--layout-bg-lighter);
    margin-bottom: 26px;
  }

  .actions-block {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    .base-button {
      box-sizing: border-box;
      width: 100%;
    }

    &:has(.base-button:nth-of-type(2)) .base-button {
      width: calc(50% - 8px);
    }
  }

  @media (max-width: 920px) {
    padding: 16px;
  }
}
</style>